/* NOTE: Run yarn generate:jsonschema after modifying this file
 *
 * You will also need to commit generated code changes
 */

export type AutoApprover = "grafana-irm" | "incidentio" | "pagerduty";
export type Directory = "azure-ad" | "entra-id" | "okta" | "workspace";

export type IdpGroup = {
  id: string;
  label: string;
  directory: Directory;
};

export type IdpGroups = {
  readonly type: "group";
  groups: IdpGroup[];
  effect: "keep" | "remove";
};

export type EscalationServices = {
  services: string[];
  integration: AutoApprover;
};

export type RequestorProfile = {
  directory: Directory;
  profileProperty?: string;
};

// DURATION_OPTIONS_SECONDS is duplicated from shared/src/configuration/constant.ts:DURATION_OPTIONS_SECONDS but the type is loosened b/c ts-json-schema-generator cannot handle binary expressions with as const type
export const DURATION_OPTIONS_SECONDS = {
  s: 1,
  m: 60,
  h: 3600,
  d: 86400,
  w: 604800,
} as const;

// DurationOption duplicated from shared/src/configuration/types.ts into this file b/c ts-json-schema-generator cannot import files
// Also ReadOnly is removed from this duplicate due to ts-json-schema-generator limitations
export type DurationOption = {
  value: string;
  /** Whole number of `unit`s; durations are parsed from user input with
   * `CUSTOM_DURATION_PATTERN` (`\d+`), so fractions are never representable,
   * and a zero or negative cap is not a meaningful limit.
   *
   * @asType integer
   * @minimum 1
   */
  time: number;
  unit: keyof typeof DURATION_OPTIONS_SECONDS;
};

/** The `ApprovalRule` members that carry a `maxDuration` cap. The type
 * parameter exists only to make the conditional distribute over the union. */
export type DurationLimitedApproval<T = ApprovalRule> = T extends unknown
  ? "maxDuration" extends keyof T
    ? T
    : never
  : never;

const DURATION_LIMITED_APPROVAL_TYPES: Record<
  DurationLimitedApproval["type"],
  true
> = {
  auto: true,
  escalation: true,
  group: true,
  p0: true,
  persistent: true,
  "requestor-profile": true,
};

export const isDurationLimited = (
  approval: ApprovalRule
): approval is DurationLimitedApproval =>
  approval.type in DURATION_LIMITED_APPROVAL_TYPES;

/** Which tenant default applies when an action's `maxDuration` is unset:
 * automatic (no human approves) → standing max, manual → tenant max */
export const APPROVAL_DURATION_CATEGORY: Record<
  DurationLimitedApproval["type"],
  "automatic" | "manual"
> = {
  auto: "automatic",
  persistent: "automatic",
  escalation: "manual",
  group: "manual",
  p0: "manual",
  "requestor-profile": "manual",
};

export type ApprovalOptions = {
  requireReason?: boolean;
  requireDuration?: boolean;
  requirePreapproval?: boolean;
};

export type UserApprovalOptions = {
  /** If true, allows approvers to approve their own requests */
  allowOneParty?: boolean;
  breakGlassApprover?: boolean;
  requireReason?: boolean;
  requireDuration?: boolean;
  requirePreapproval?: boolean;
};

/** Caps the requested duration this policy approval rule can grant */
export type DurationLimited = {
  maxDuration?: DurationOption;
};

export type Filter = BooleanFilter | PatternFilter;

/** Represents a pattern that can match a named item
 *
 * Semantics:
 *  - If no rule is defined for a rule, it will apply to all resources
 *    in the integration.
 *  - If only a single filter is defined for a rule, the rule
 *    itself will apply if and only if:
 *    - "effect" is "keep" and the resource matches the filter
 *    - "effect" is "remove" and the resource does _not_ match the filter
 *  - If multiple filters are defined for a rule, the rule will apply if
 *    and only if:
 *    - No "removeAll" rules are defined that apply to the resource.
 *    - No "remove" rule matches the resource.
 *    - At least one "keep" rule matches the resource.
 */
export type PatternFilter =
  | {
      effect: "keep" | "remove";
      // Ideally "key" would be a parameterized value; however, typeconv just ignores parameterized types
      key: string;
      pattern: string;
    }
  | { effect: "removeAll" };

/**
 * Represents a boolean filter that matches true or false values.
 * Value is a unique property that only exists on the boolean filter and is used to discriminate between the two filter types.
 */
export type BooleanFilter =
  | {
      effect: "keep" | "remove";
      value: boolean;
    }
  | { effect: "removeAll" };

/** Rule matches all objects */
export type AnyRule = {
  readonly type: "any";
};

/** An automated integration can approve the request */
export type AutoApprovalRule = DurationLimited & {
  readonly type: "auto";
  integration: AutoApprover;
  options?: ApprovalOptions;
};
/** P0 managers can approve the request */
export type P0ApprovalRule = DurationLimited & {
  readonly type: "p0";
  options?: UserApprovalOptions;
};
/** Any member of the group can approve the request */
export type GroupApprovalRule = DurationLimited &
  IdpGroups & {
    options?: UserApprovalOptions;
  };
/** No-one can ever approve this request */
export type DenyApprovalRule = {
  readonly type: "deny";
};

/** Represents a persistent access grant */
export type PersistentApprovalRule = DurationLimited & {
  readonly type: "persistent";
  options?: UserApprovalOptions;
};

/** Defines how approval requests are escalated  */
export type EscalationApprovalRule = DurationLimited &
  EscalationServices & {
    readonly type: "escalation";
    options?: UserApprovalOptions;
  };

/** Allows approval by a user specified by a field in the requestor's IDP profile */
export type RequestorProfileApprovalRule = DurationLimited &
  RequestorProfile & {
    readonly type: "requestor-profile";
    options?: UserApprovalOptions;
  };

// reason is added to individual approvals for request validation
export type ApprovalRule =
  | AutoApprovalRule
  | DenyApprovalRule
  | EscalationApprovalRule
  | GroupApprovalRule
  | P0ApprovalRule
  | PersistentApprovalRule
  | RequestorProfileApprovalRule;

/** Rule matches when requestor is in a group */
export type GroupRequestorRule = IdpGroups;
/** Rule matches when requestor is a specific user */
export type UserRequestorRule = {
  readonly type: "user";
  uid: string;
};

/** Rule matches only when the request has no human user (agent-only session) and is single-hop (not A2a). Thus actor agent will be same as the subject */
export type SameActorAndSubject = {
  readonly type: "same-as-subject";
};

export type AgentClientRequestorRule = {
  readonly type: "agent-client";
  readonly clientId: string;
};
export type AgentProviderRule = {
  readonly type: "provider";
  readonly providerId: string;
  readonly subjectPattern?: string;
};
export type AgentOwnerRule = {
  readonly type: "agent-owner";
  readonly owner: string;
};
export type AgentOwnerGroupRule = {
  readonly type: "owner-group";
  readonly groups: IdpGroups;
};

export type AgentRule =
  | AgentClientRequestorRule
  | AgentOwnerGroupRule
  | AgentOwnerRule
  | AgentProviderRule
  | AnyRule;

/** Rule matches when the request's own subject (there is no human user) is
 * itself a specific agent — unlike {@link SameActorAndSubject}, which only asserts
 * that no human is present, this also constrains *which* agent it is.
 */
export type AgenticActorRule = AgentRule | SameActorAndSubject;

export type AgentSubjectRule = {
  readonly type: "agent";
  readonly agent: AgentRule;
};

export type UserSubjectRule = {
  readonly type: "user";
  readonly user: AnyRule | GroupRequestorRule | UserRequestorRule;
};

export type AgenticSubjectRule = AgentSubjectRule | AnyRule | UserSubjectRule;

export type AgenticRequestorRule = {
  readonly type: "agentic";
  readonly subject: AgenticSubjectRule;
  readonly actor: AgenticActorRule;
};

export type RequestorRule =
  | AgenticRequestorRule
  | AnyRule
  | GroupRequestorRule
  | UserRequestorRule;

/** Rule matches when resource is an integrated IAM service */
// NB. Service rule filter keys should correspond to subcommands
export type IntegrationResourceRule = {
  readonly type: "integration";
  readonly service: string;
  readonly accessType?: string;
  readonly filters?: Record<string, BooleanFilter | PatternFilter>;
};

export type ResourceRule = AnyRule | IntegrationResourceRule;

/** A single access-control policy
 *
 * Rules match based on `requestor` and `resource`, and all approval rules
 * can be used to approve access, with the exception that, if one of the
 * approval rules is a "deny" rule, no access will be permitted for matching
 * `requestor` and `resource` pairs.
 */
export type Policy = {
  /** A human readable name for this policy */
  name?: string;
  /** Whether or not the policy should be evaluated; if false or undefined, the rule will be evaluated */
  disabled?: boolean;
  requestor: RequestorRule;
  resource: ResourceRule;
  approval: ApprovalRule[];
};

/** An entire policy configuration
 *
 * Currently the only supported configuration is the set of policies
 */
export type PolicyConfig = {
  rules: Policy[];
};
